import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  ButtonGroup,
  Fade,
  IconButton,
  Avatar as MuiAvatar,
  Paper,
  Typography,
  Zoom,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useState } from "react";

import { formatDateTimeString } from "@helpers/utils";

import DescriptiveTooltip from "../../../components/DescriptiveTooltip";
import Avatar from "../../app/components/Avatar";
import MessageContent from "./MessageContent";
import TypingIndicator from "./TypingIndicator";

const MessageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isUser",
})<{ isUser?: boolean }>(({ theme, isUser }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(2),
  flexDirection: isUser ? "row-reverse" : "row",
  marginBottom: theme.spacing(2),
  opacity: 1,
  transform: "translateY(0)",
  transition: theme.transitions.create(["opacity", "transform"], {
    duration: theme.transitions.duration.standard,
  }),
}));

const MessageBubble = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "isUser" && prop !== "hasCodeBlocks",
})<{ isUser?: boolean; hasCodeBlocks?: boolean }>(
  ({ theme, isUser, hasCodeBlocks }) => ({
    padding: theme.spacing(1.5),
    maxWidth: hasCodeBlocks ? "85%" : "70%",
    backgroundColor: isUser
      ? theme.palette.primary.main
      : theme.palette.grey[50],
    color: isUser
      ? theme.palette.primary.contrastText
      : theme.palette.text.primary,
    borderRadius: isUser
      ? theme.spacing(2, 2, 0, 2)
      : theme.spacing(2, 2, 2, 0),
    boxShadow: theme.shadows[1],
    position: "relative",
    "&:hover .message-actions": {
      opacity: 1,
    },
    transition: theme.transitions.create(["box-shadow", "transform"], {
      duration: theme.transitions.duration.short,
    }),
    "&:hover": {
      boxShadow: theme.shadows[3],
    },
  }),
);

const MessageAvatar = styled(MuiAvatar, {
  shouldForwardProp: (prop) => prop !== "isUser",
})<{ isUser?: boolean }>(({ theme, isUser }) => ({
  backgroundColor: isUser
    ? theme.palette.secondary.main
    : theme.palette.primary.main,
  color: isUser
    ? theme.palette.secondary.contrastText
    : theme.palette.primary.contrastText,
  boxShadow: theme.shadows[2],
}));

const MessageActions = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isUser",
})<{ isUser?: boolean }>(({ theme, isUser }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  left: isUser ? theme.spacing(-4) : "auto",
  right: isUser ? "auto" : theme.spacing(-8),
  opacity: 0,
  transition: theme.transitions.create("opacity", {
    duration: theme.transitions.duration.shorter,
  }),
  backgroundColor: "transparent",
  display: "flex",
  alignItems: "center",
  zIndex: 2,
}));

const TimeStamp = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(0.5),
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
  padding: theme.spacing(0, 1),
}));

interface MessageProps {
  text: string;
  isUser: boolean;
  timestamp: Date;
  isTyping?: boolean;
  onRetry?: () => void;
}

const Message = ({ text, isUser, timestamp, onRetry }: MessageProps) => {
  const formattedTimestamp = formatDateTimeString(timestamp.toISOString());

  const hasCodeBlocks = text.includes("```");

  const [showTimestamp, setShowTimestamp] = useState(false);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimated(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Zoom in={animated} style={{ transitionDelay: animated ? "100ms" : "0ms" }}>
      <MessageContainer isUser={isUser}>
        <MessageAvatar isUser={isUser}>
          {isUser ? <Avatar /> : <AutoAwesome />}
        </MessageAvatar>
        <Box
          display="flex"
          flexDirection="column"
          alignItems={isUser ? "flex-end" : "flex-start"}
          sx={{ width: "100%" }}
        >
          <MessageBubble
            isUser={isUser}
            elevation={isUser ? 1 : 2}
            hasCodeBlocks={hasCodeBlocks}
            onMouseEnter={() => setShowTimestamp(true)}
            onMouseLeave={() => setShowTimestamp(false)}
          >
            {!isUser && text.length === 0 ? (
              <TypingIndicator color="rgba(0, 0, 0, 0.6)" />
            ) : (
              <MessageContent text={text} isUser={isUser} />
            )}

            <MessageActions isUser={isUser} className="message-actions">
              <ButtonGroup
                size="small"
                variant="outlined"
                sx={{ background: "white", borderRadius: 1 }}
              >
                {isUser && onRetry && (
                  <DescriptiveTooltip title="Retry message">
                    <IconButton size="small" onClick={onRetry}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </DescriptiveTooltip>
                )}
              </ButtonGroup>
            </MessageActions>
          </MessageBubble>

          <Fade in={showTimestamp} timeout={300}>
            <TimeStamp>
              <AccessTimeIcon sx={{ fontSize: "0.75rem" }} />
              <Typography variant="caption" color="text.secondary">
                {formattedTimestamp}
              </Typography>
            </TimeStamp>
          </Fade>
        </Box>
      </MessageContainer>
    </Zoom>
  );
};

export default Message;
